import { createFileRoute, Link } from '@tanstack/react-router';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { FiPlus, FiBriefcase } from 'react-icons/fi';
import { useGetPublicServices, type ServiceItem } from '../api/services';
import { useAuth } from '../hooks/useAuth';
import { Avatar } from '../components/ui/Avatar';
import { useState, useEffect } from 'react';
import { LoadingLogo } from '../components/common/LoadingLogo';
import { formatServicePrice } from '../utils/price';

export const Route = createFileRoute('/services/')({
  component: ServicesPage,
});

function ServicesPage() {
  const { isAuthenticated } = useAuth();

  // Paginated public services state
  const [publicPage, setPublicPage] = useState(1);
  const [allPublicServices, setAllPublicServices] = useState<ServiceItem[]>([]);

  const publicServicesResult = useGetPublicServices(publicPage);

  const isLoading = publicServicesResult.isLoading;
  const isFetching = publicServicesResult.isFetching;
  const hasMore = !!publicServicesResult.data?.next;

  // Accumulate public service pages
  useEffect(() => {
    if (!publicServicesResult.data?.results?.length) return;
    if (publicPage === 1) {
      setAllPublicServices(publicServicesResult.data.results);
    } else {
      setAllPublicServices((prev) => [...prev, ...publicServicesResult.data!.results]);
    }
  }, [publicServicesResult.data, publicPage]);

  // Reset when auth state changes (optional, but keeps it clean if user logs in/out)
  useEffect(() => {
    setPublicPage(1);
    setAllPublicServices([]);
  }, [isAuthenticated]);

  // Accumulated state → direct query data → []
  const services: ServiceItem[] =
    allPublicServices.length > 0 ? allPublicServices : (publicServicesResult.data?.results ?? []);

  // Show skeleton only when genuinely loading with no data to display yet
  const showSkeleton = isLoading && services.length === 0;

  if (showSkeleton) {
    return <LoadingLogo />;
  }

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />
      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-white text-2xl sm:text-[28px] font-bold">Services</h1>
            <p className="text-zinc-500 text-sm mt-1">
              Explore services from our global community of creators
            </p>
          </div>
          {isAuthenticated && (
            <Link
              to="/services/create"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-[14px] font-semibold px-5 py-2.5 rounded-full transition-all active:scale-95 self-start sm:self-auto"
            >
              <FiPlus size={16} strokeWidth={2.5} />
              New Service
            </Link>
          )}
        </div>

        {services.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center gap-6">
            <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center text-zinc-600">
              <FiBriefcase size={34} />
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-white text-xl font-semibold">No services yet</p>
              <p className="text-zinc-500 max-w-xs leading-relaxed text-sm">
                {isAuthenticated
                  ? 'Create your first service to show what you offer and attract gigs.'
                  : 'No public services available yet.'}
              </p>
            </div>
            {isAuthenticated && (
              <Link
                to="/services/create"
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-[14px] font-semibold px-6 py-3 rounded-full transition-all active:scale-95"
              >
                <FiPlus size={16} />
                Create Service
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((service: ServiceItem) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
            {/* Load more skeleton while fetching */}
            {isFetching && services.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="rounded-2xl border border-white/5 bg-zinc-900/60 animate-pulse p-4 flex flex-col gap-3 h-36"
                  />
                ))}
              </div>
            )}
            {/* Load more button */}
            {hasMore && !isFetching && (
              <div className="flex justify-center mt-8">
                <button
                  className="px-10 py-3 rounded-full border border-white/20 text-white text-sm font-medium hover:bg-white/5 transition-all active:scale-95"
                  onClick={() => setPublicPage((prev) => prev + 1)}
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </main>
      <BottomNav />
    </div>
  );
}

function ServiceCard({ service }: { service: ServiceItem }) {
  const creatorIdentifier = service.creator?.username || String(service.creator?.id || '');
  const allTags = [
    ...service.user_types.map((t) => t.name),
    ...service.user_tags.map((t) => t.name),
  ];
  // Show fewer tags on card to prevent crowding; allow them to wrap
  const visibleTags = allTags.slice(0, 2);
  const hasExtra = allTags.length > 2;

  return (
    <Link
      to="/services/$serviceId"
      params={{ serviceId: String(service.id) }}
      className="no-underline block rounded-[24px] bg-zinc-900/40 border border-white/5 hover:border-white/15 hover:bg-zinc-900/80 transition-all duration-300 group overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-black/50"
    >
      <div className="p-5 flex flex-col gap-4">
        {/* Top row: avatar + tags */}
        <div className="flex items-start justify-between gap-3">
          <div className="shrink-0">
            {creatorIdentifier ? (
              <Link
                to="/profile/$identifier"
                params={{ identifier: creatorIdentifier }}
                className="block group/avatar"
                onClick={(e) => e.stopPropagation()}
              >
                <Avatar
                  src={service.creator?.image}
                  alt={service.creator?.full_name}
                  name={service.creator?.full_name || service.creator?.username}
                  size="lg"
                  className="border border-white/10 group-hover/avatar:border-white/30 transition-colors"
                />
              </Link>
            ) : (
              <Avatar
                src={service.creator?.image}
                alt={service.creator?.full_name}
                name={service.creator?.full_name || service.creator?.username}
                size="lg"
                className="border border-white/10"
              />
            )}
          </div>

          {allTags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 justify-end flex-1 min-w-0 pt-1">
              {visibleTags.map((tag, i) => (
                <span
                  key={i}
                  className="px-3 py-1 rounded-full bg-zinc-800/80 border border-white/5 text-zinc-300 text-[10px] font-semibold whitespace-nowrap truncate max-w-[100px] sm:max-w-[130px]"
                >
                  {tag}
                </span>
              ))}
              {hasExtra && (
                <span className="px-2.5 py-1 rounded-full bg-zinc-900 border border-white/5 text-zinc-500 text-[10px] font-bold whitespace-nowrap">
                  +{allTags.length - 2}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Title & Info */}
        <div className="space-y-1.5">
          <h3 className="text-white font-bold text-[17px] leading-tight group-hover:text-blue-400 transition-colors truncate">
            {service.title}
          </h3>

          {/* Description */}
          {service.description ? (
            <p className="text-zinc-500 text-[13px] leading-relaxed line-clamp-2 h-10 italic">
              {service.description}
            </p>
          ) : (
            <div className="h-10" /> // Placeholder to keep height consistent
          )}
        </div>

        {/* Bottom row: Price */}
        <div className="pt-2 mt-auto border-t border-white/5 flex items-center justify-between">
          {service.price ? (
            <span className="text-[#a78bfa] font-bold text-[15px]">
              {formatServicePrice(service.price)}
            </span>
          ) : (
            <span className="text-zinc-600 text-[13px] font-medium">Negotiable</span>
          )}
          <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-zinc-400 group-hover:bg-blue-500 group-hover:text-white transition-all">
            <FiBriefcase size={14} />
          </div>
        </div>
      </div>
    </Link>
  );
}
